import { api } from './api';

/**
 * Lectures de l'écran Argent qui ne sont pas dans `financeAPI`.
 *
 * `position`: soldes des comptes à aujourd'hui, derniers mouvements, argent
 * chez les chauffeurs, dettes fournisseurs et écritures en attente — une
 * lecture directe du journal (`GET /finance/position`), au lieu du bilan
 * mensuel complet des rapports qui mettait 13 à 18 secondes.
 */
export async function positionArgent() {
  const response = await api.get('/finance/position');
  return response?.data?.data ?? null;
}

const corps = (response) => response?.data?.data ?? null;

/**
 * Livre de caisse d'un compte, trié, soldé et paginé par le serveur:
 * `{ account, period, totals, page, page_size, total, items }`.
 * `params`: `page`, `page_size`, `q`, `sens` (entrees, sorties, transferts,
 * controles), `du`, `au` (AAAA-MM-JJ).
 */
export async function livreDeCaisse(compteId, params = {}) {
  const propres = Object.fromEntries(Object.entries(params).filter(([, valeur]) => valeur !== '' && valeur != null));
  return corps(await api.get(`/finance/accounts/${encodeURIComponent(compteId)}/ledger`, { params: propres }));
}

/** Une écriture et ce qui l'entoure (comptes, tiers, affectations, annulation). */
export async function detailEcriture(operationId) {
  return corps(await api.get(`/finance/operations/${encodeURIComponent(operationId)}`));
}

/** Complète (ou corrige) le solde d'ouverture d'un compte, avec un motif. */
export async function fixerSoldeOuverture(compteId, { montant, date, motif, cle }) {
  return corps(await api.put(`/finance/accounts/${encodeURIComponent(compteId)}/opening-balance`, {
    opening_balance: Number(montant),
    opening_date: date,
    reason: motif,
    idempotency_key: cle,
  }));
}

/** Enregistre un comptage de caisse: le serveur garde le solde du journal et l'écart. */
export async function compterCaisse(compteId, { montant, motif, cle }) {
  return corps(await api.post(`/finance/accounts/${encodeURIComponent(compteId)}/checks`, {
    declared_balance: Number(montant),
    reason: motif,
    idempotency_key: cle,
  }));
}

/** Change l'état d'une écriture (`CONFIRM` ou `REVERSE`), avec un motif. */
export async function changerEtatEcriture(operationId, { action, motif, cle, compte }) {
  return corps(await api.post(`/finance/operations/${encodeURIComponent(operationId)}/state`, {
    action,
    reason: motif,
    idempotency_key: cle,
    ...(compte ? { account_id: compte } : {}),
  }));
}

const sansVides = (params) => Object.fromEntries(Object.entries(params || {}).filter(([, valeur]) => valeur !== '' && valeur != null && valeur !== false));

/**
 * Facturé, avoirs, encaissé et reste dû par client, toutes périodes, paginés
 * par le serveur: `{ page, page_size, total, totals, items }`.
 * `params`: `page`, `page_size`, `q`, `avec_reste`, `tri` (reste, anciennete, nom, facture).
 */
export async function soldesClients(params = {}) {
  return corps(await api.get('/finance/clients/balances', { params: sansVides(params) }));
}

/**
 * Avances, frais justifiés, reliquats rendus et reste chez chaque chauffeur,
 * paginés par le serveur. `params`: `page`, `page_size`, `q`, `a_justifier`,
 * `tri` (reste, nom, avance).
 */
export async function soldesChauffeurs(params = {}) {
  return corps(await api.get('/finance/drivers/balances', { params: sansVides(params) }));
}

/**
 * Factures reçues, avoirs, payé et reste à payer par fournisseur (transporteurs
 * des lots et des états de transport compris), paginés par le serveur.
 * `params`: `page`, `page_size`, `q`, `avec_reste`, `tri` (reste, anciennete, nom, facture).
 */
export async function soldesFournisseurs(params = {}) {
  return corps(await api.get('/finance/suppliers/balances', { params: sansVides(params) }));
}

/** Factures ouvertes d'un client, de la plus ancienne à la plus récente. */
export async function facturesOuvertesClient(clientId) {
  return corps(await api.get(`/finance/clients/${encodeURIComponent(clientId)}/open-invoices`));
}

/** Comptes de caisse et de banque de la société. */
export async function comptesTresorerie() {
  const response = await api.get('/finance/accounts');
  const corps = response?.data;
  return Array.isArray(corps) ? corps : corps?.data || [];
}

// Le dernier compte utilisé est proposé d'abord (confort du poste, rien de plus).
const CLE_DERNIER_COMPTE = 'argent-dernier-compte';
const lireDernierCompte = () => {
  try {
    return window.localStorage.getItem(CLE_DERNIER_COMPTE) || '';
  } catch (e) {
    return '';
  }
};

/** Retient le compte d'un mouvement d'argent, pour le proposer la fois suivante. */
export const retenirCompte = (id) => {
  try {
    if (id) window.localStorage.setItem(CLE_DERNIER_COMPTE, id);
  } catch (e) {
    // stockage indisponible: on proposera la caisse principale
  }
};

/** Compte proposé pour un mouvement d'argent: le dernier utilisé, sinon la première caisse. */
export const compteParDefaut = (comptes, dernier = lireDernierCompte()) => {
  const liste = Array.isArray(comptes) ? comptes : [];
  if (dernier && liste.some((row) => row.id === dernier)) return dernier;
  return (liste.find((row) => row.account_type === 'CASH') || liste[0])?.id || '';
};

/** Natures d'écriture qui font bouger l'argent d'un compte (caisse ou banque). */
export const NATURES_AVEC_COMPTE = [
  'CUSTOMER_RECEIPT',
  'CUSTOMER_REFUND',
  'SUPPLIER_PAYMENT',
  'SUPPLIER_REFUND',
  'DRIVER_ADVANCE',
  'DRIVER_RETURN',
  'DRIVER_REIMBURSEMENT',
  'EXPENSE',
  'INCOME',
  'TRANSFER',
];

/** Sens d'une écriture sur ce que doit le tiers (fournisseur : ce qu'on lui doit). */
export const SIGNES_TIERS = {
  HISTORICAL_BALANCE: 1,
  HISTORY_RECONCILIATION: 1,
  CUSTOMER_INVOICE: 1,
  CUSTOMER_ADJUSTMENT: 1,
  CUSTOMER_RECEIPT: -1,
  CUSTOMER_CREDIT: -1,
  CUSTOMER_REFUND: 1,
  SUPPLIER_BILL: 1,
  SUPPLIER_PAYMENT: -1,
  SUPPLIER_CREDIT: -1,
  SUPPLIER_REFUND: 1,
  DRIVER_ADVANCE: 1,
  DRIVER_EXPENSE: -1,
  DRIVER_RETURN: -1,
  DRIVER_REIMBURSEMENT: 1,
  CLAIM: 1,
};

/** Sens d'une écriture sur un compte de caisse ou de banque. */
export const SIGNES_CAISSE = {
  CUSTOMER_RECEIPT: 1,
  CUSTOMER_REFUND: -1,
  SUPPLIER_PAYMENT: -1,
  SUPPLIER_REFUND: 1,
  DRIVER_ADVANCE: -1,
  DRIVER_RETURN: 1,
  DRIVER_REIMBURSEMENT: -1,
  EXPENSE: -1,
  INCOME: 1,
  TRANSFER: -1,
  CASH_ASSIGNMENT: 0,
};

/** +1 entrée, −1 sortie, 0 sans effet — même règle que le serveur. */
export const signeCaisse = (operation, compteId) => {
  if (operation?.target_account_id && operation.target_account_id === compteId) return 1;
  if (operation?.kind === 'CASH_ASSIGNMENT') return Number(operation.details?.direction) || 0;
  return SIGNES_CAISSE[operation?.kind] || 0;
};

/**
 * Reprises historiques à faire: soldes clients à rapprocher et achats des
 * stations pas encore repris. Deux lectures légères; l'une en échec ne masque
 * pas l'autre (elle compte alors pour zéro).
 */
export async function reprisesAFaire() {
  const [historique, achats] = await Promise.allSettled([
    api.get('/finance/history-review'),
    api.get('/finance/purchases-to-review'),
  ]);
  const liste = (resultat) => {
    if (resultat.status !== 'fulfilled') return [];
    const corps = resultat.value?.data;
    return Array.isArray(corps) ? corps : corps?.data || [];
  };
  return {
    soldesClients: liste(historique).filter((ligne) => ligne.requires_review).length,
    achats: liste(achats).filter((ligne) => !ligne.adopted).length,
  };
}
